import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Quote, Check, X } from "lucide-react";
import { toast } from "sonner";

const IMG = [
    "https://images.unsplash.com/photo-1622253694238-3b22139576c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxibGFjayUyMG51cnNlJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg1NjE5NTk4fDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1643297654416-05795d62e39c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxibGFjayUyMG51cnNlJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg1NjE5NTk4fDA&ixlib=rb-4.1.0&q=85",
];

export default function PeopleStories() {
    const { user } = useAuth();
    const isMgr = ["hr_leadership", "admin"].includes(user?.role);
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", body: "", author_display: "", anonymous: false });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const load = () => api.get("/stories").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.title || !form.body) { toast.error("Title and story are required"); return; }
        try { await api.post("/stories", form); toast.success("Story submitted for moderation"); setOpen(false); setForm({ title: "", body: "", author_display: "", anonymous: false }); load(); }
        catch { toast.error("Could not submit story"); }
    };
    const moderate = async (id, action) => {
        try { await api.patch(`/stories/${id}/moderate?action=${action}`); toast.success(`Story ${action}d`); load(); }
        catch { toast.error("Action failed"); }
    };

    return (
        <div className="space-y-6" data-testid="stories-page">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-extrabold text-nhs-ink">People Stories</h1>
                    <p className="text-slate-500 mt-1">Lived experiences shared by BAME colleagues. Moderated for safety.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button className="bg-nhs-blue hover:bg-nhs-darkblue text-white gap-2" data-testid="share-story-button"><Plus className="h-4 w-4" /> Share your story</Button></DialogTrigger>
                    <DialogContent data-testid="story-dialog">
                        <DialogHeader><DialogTitle className="font-heading">Share your story</DialogTitle></DialogHeader>
                        <div className="space-y-4 py-2">
                            <div><Label>Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" data-testid="story-title-input" /></div>
                            <div><Label>Your story</Label><Textarea rows={6} value={form.body} onChange={(e) => set("body", e.target.value)} className="mt-1.5" data-testid="story-body-input" /></div>
                            <div><Label>Display name (optional)</Label><Input value={form.author_display} onChange={(e) => set("author_display", e.target.value)} disabled={form.anonymous} className="mt-1.5" data-testid="story-author-input" /></div>
                            <div className="flex items-center gap-2"><Checkbox id="sanon" checked={form.anonymous} onCheckedChange={(v) => set("anonymous", !!v)} data-testid="story-anonymous-checkbox" /><Label htmlFor="sanon" className="text-sm font-normal cursor-pointer">Post anonymously</Label></div>
                        </div>
                        <DialogFooter><Button onClick={submit} className="bg-nhs-blue hover:bg-nhs-darkblue text-white" data-testid="submit-story-button">Submit</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {items.length === 0 ? (
                <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-400" data-testid="empty-stories">No stories yet. Be the first to share.</div>
            ) : (
                <div className="grid md:grid-cols-2 gap-6">
                    {items.map((st, i) => (
                        <div key={st.id} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid={`story-${st.id}`}>
                            <img src={IMG[i % IMG.length]} alt="NHS colleague" className="h-40 w-full object-cover" />
                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <Quote className="h-5 w-5 text-nhs-teal" />
                                    {st.status !== "approved" && <Badge className="bg-nhs-amber/20 text-[#8a6400] border-0 capitalize">{st.status}</Badge>}
                                </div>
                                <h3 className="font-heading font-semibold text-lg text-nhs-ink mt-3">{st.title}</h3>
                                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{st.body}</p>
                                <p className="text-xs text-slate-400 mt-4">— {st.author_display}</p>
                                {isMgr && st.status === "pending" && (
                                    <div className="flex gap-2 mt-4">
                                        <Button size="sm" onClick={() => moderate(st.id, "approve")} className="bg-nhs-teal hover:bg-[#00857c] text-white gap-1" data-testid={`approve-${st.id}`}><Check className="h-4 w-4" /> Approve</Button>
                                        <Button size="sm" variant="outline" onClick={() => moderate(st.id, "reject")} className="border-slate-300 gap-1" data-testid={`reject-${st.id}`}><X className="h-4 w-4" /> Reject</Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
